import { useEffect, useMemo, useRef, useState } from 'react'
import type { BacktestMetrics, MonteCarloStats, EquityPoint, HistogramData } from './types'
import type { WorkerInput, WorkerOutput } from './monteCarlo.worker'

export interface UseMonteCarloSimulationOptions {
  tradeReturns?: number[]
  simulations: number
  initialCapital: number
  backtest: BacktestMetrics
  monteCarlo: MonteCarloStats
}

export interface UseMonteCarloSimulationResult {
  loading: boolean
  error: string | null
  equityData: EquityPoint[]
  netProfitHistogram: HistogramData | null
  maxDrawdownHistogram: HistogramData | null
}

function generateDemoReturns(totalTrades: number, expectancy: number, volatility: number): number[] {
  const returns: number[] = []
  for (let i = 0; i < totalTrades; i++) {
    const r = (Math.random() - 0.45) * volatility + expectancy
    returns.push(Number(r.toFixed(5)))
  }
  return returns
}

export function useMonteCarloSimulation({
  tradeReturns,
  simulations,
  initialCapital,
  backtest,
  monteCarlo,
}: UseMonteCarloSimulationOptions): UseMonteCarloSimulationResult {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [equityData, setEquityData] = useState<EquityPoint[]>([])
  const [netProfitHistogram, setNetProfitHistogram] = useState<HistogramData | null>(null)
  const [maxDrawdownHistogram, setMaxDrawdownHistogram] = useState<HistogramData | null>(null)
  const workerRef = useRef<Worker | null>(null)

  // Stabilize inputs so the worker does not restart on every parent render.
  const returns = useMemo(
    () => tradeReturns ?? generateDemoReturns(20, backtest.expectancy, 0.08),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(tradeReturns), backtest.expectancy]
  )

  const monteCarloRef = useRef(monteCarlo)
  monteCarloRef.current = monteCarlo

  useEffect(() => {
    setLoading(true)
    setError(null)

    const worker = new Worker(new URL('./monteCarlo.worker.ts', import.meta.url), { type: 'module' })
    workerRef.current = worker

    worker.onmessage = (event: MessageEvent<WorkerOutput>) => {
      const { equityPercentiles, netProfitHistogram, maxDrawdownHistogram } = event.data
      const currentMc = monteCarloRef.current

      setEquityData(
        equityPercentiles.map((p) => ({
          tradeIndex: p.tradeIndex,
          median: p.median,
          p5: p.p5,
          p10: p.p10,
          p90: p.p90,
          p95: p.p95,
          initialCapital,
        }))
      )

      setNetProfitHistogram({
        bins: netProfitHistogram.bins,
        median: currentMc.netProfit.median,
        p5: currentMc.netProfit.p5,
        p95: currentMc.netProfit.p95,
      })

      setMaxDrawdownHistogram({
        bins: maxDrawdownHistogram.bins,
        median: currentMc.maxDrawdown.median,
        p5: currentMc.maxDrawdown.p5,
        p95: currentMc.maxDrawdown.p95,
      })

      setLoading(false)
    }

    worker.onerror = (err) => {
      setError(err.message)
      setLoading(false)
    }

    const input: WorkerInput = { tradeReturns: returns, simulations, initialCapital }
    worker.postMessage(input)

    return () => {
      worker.terminate()
      workerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(returns), simulations, initialCapital])

  return { loading, error, equityData, netProfitHistogram, maxDrawdownHistogram }
}
